/**
 * ================================================================================================
 *  A QR CODE, GENERATED IN THIS BROWSER AND NOWHERE ELSE. **§QR, 2026-10-25.**
 * ================================================================================================
 *
 * Owner approval, verbatim and conditional:
 *
 *     *"מאשר הוספת רכיב QR לצורך רישום MFA, בתנאי שהיצירה מקומית וללא שליחת המפתח לשירות חיצוני."*
 *
 * THE CONDITION IS THE DESIGN, so here is exactly how it is met and how to check it:
 *
 *   · `qrcode-generator` is a pure-JavaScript encoder with **zero runtime dependencies** (verified
 *     against the installed `node_modules/qrcode-generator/package.json`, not the registry page),
 *     MIT, shipping its own type declarations. It is bundled by Vite into the app.
 *   · It computes the module matrix with arithmetic. It opens no socket, reads no URL and touches
 *     no DOM outside what this component renders. There is no image service, no CDN, no
 *     `chart.googleapis.com` — the pattern that would have sent the shared secret to a third party
 *     in a query string, which is precisely what the Owner ruled out.
 *   · The value this component receives is the `otpauth://` URI, and **that URI CONTAINS THE
 *     SHARED SECRET**. So it is never logged, never written to storage, never put in an attribute
 *     a devtools pane lists casually, and never handed to anything but the encoder.
 *
 * ── WHY THE SVG IS BUILT FROM `isDark`, NOT FROM `createSvgTag` ─────────────────────────────────
 *
 * The library offers `createSvgTag()` and `createDataURL()`. Both return a STRING that would have
 * to reach the DOM through `dangerouslySetInnerHTML` or an `img src`, and a secret-bearing payload
 * is the last place to introduce an innerHTML path. Reading the matrix and emitting React elements
 * keeps the whole thing inside React's escaping, and it is less code than the alternative.
 *
 * ONE `<path>`, not one `<rect>` per module: a version-2 code is 25x25 and a version-3 is 29x29, so
 * the rect-per-module form is six to eight hundred elements for a picture that never changes.
 *
 * ── IT IS AN IMAGE, AND IT IS NOT THE ONLY WAY IN ───────────────────────────────────────────────
 *
 * `role="img"` with a label, because that is what it is. The label does NOT read the payload aloud:
 * a screen reader announcing a shared secret into a room is the same disclosure as printing it.
 * Whoever cannot scan it uses the manual key, which the enrolment screen shows beside it — the QR
 * is a convenience over that path, never a replacement for it.
 */

import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

export interface QrCodeProps {
  /** The payload. For enrolment this is the `otpauth://` URI, which CONTAINS the shared secret. */
  readonly value: string;
  /** What a screen reader says. Never the payload. */
  readonly labelHe: string;
  /** Rendered edge length in CSS pixels. The matrix is square, so one number is enough. */
  readonly sizePx?: number;
}

/**
 * `'M'` — about 15% of the code may be damaged and still read.
 *
 * `'L'` makes a smaller picture and `'H'` a denser one. M is what authenticator enrolment codes
 * conventionally use, and the trade is real: this code is read once, from a clean screen, by a
 * phone held 20cm away, so the failure it must survive is a slightly dirty camera rather than a
 * printed label that has been folded. Higher correction would mean more modules in the same
 * physical area, which makes it HARDER for a low-end camera, not easier.
 */
const ERROR_CORRECTION = 'M';

/** `0` asks the library to pick the smallest version that fits the payload. */
const AUTO_VERSION = 0;

/** Quiet zone, in modules. The spec requires four; a code without it is unreliable to scan. */
const QUIET_ZONE = 4;

export function QrCode({ value, labelHe, sizePx = 200 }: QrCodeProps): JSX.Element | null {
  /*
   * MEMOISED ON THE VALUE. Encoding is pure arithmetic over a short string, but it runs on every
   * keystroke in the confirmation field otherwise — this screen re-renders on each one.
   */
  const drawn = useMemo(() => {
    if (value === '') return null;
    try {
      const qr = qrcode(AUTO_VERSION, ERROR_CORRECTION);
      qr.addData(value);
      qr.make();

      const count = qr.getModuleCount();
      const span = count + QUIET_ZONE * 2;

      /*
       * ONE PATH, in module units: `M x y h1 v1 h-1 z` per dark module. The viewBox then scales the
       * whole thing to whatever `sizePx` asks for, so the picture is resolution-independent and the
       * arithmetic here never deals in pixels.
       */
      const parts: string[] = [];
      for (let row = 0; row < count; row += 1) {
        for (let col = 0; col < count; col += 1) {
          if (qr.isDark(row, col)) {
            parts.push(`M${col + QUIET_ZONE} ${row + QUIET_ZONE}h1v1h-1z`);
          }
        }
      }
      return { span, path: parts.join('') };
    } catch {
      /*
       * The payload was too long for any version, or the library refused it. Returning null makes
       * the caller fall back to the manual key, which is the path that always works. It is NOT
       * logged: the only interesting thing about the failure is the payload, and the payload is the
       * secret.
       */
      return null;
    }
  }, [value]);

  if (drawn === null) return null;

  return (
    <svg
      className="qr"
      role="img"
      aria-label={labelHe}
      width={sizePx}
      height={sizePx}
      viewBox={`0 0 ${drawn.span} ${drawn.span}`}
      /*
       * The SVG coordinate system is not mirrored by `direction`, so an RTL document cannot flip
       * this picture and nothing here needs to say so. (`dir` is not in React's SVG prop types, and
       * setting it would have been a no-op dressed as a precaution.)
       */
      shapeRendering="crispEdges"
    >
      {/* The quiet zone has to be WHITE, not transparent: on a tinted panel a scanner loses the edge. */}
      <rect width={drawn.span} height={drawn.span} fill="#ffffff" />
      <path d={drawn.path} fill="#000000" />
    </svg>
  );
}
